import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  DISPLAY_LABEL,
  HELPER_TEXT,
  LICENSE_SCOPE,
  TAG_IDENTIFIER,
} from "./consts";
import { useLocation, useNavigate } from "react-router-dom";
import { addSchema, AddZoneFormValues } from "./validations";
import { toast } from "@/components/Toast";
import { useAddPropertyZoneMutation } from "@/services";
import { getBaseRoute, getRequiredFields } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

const useAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = getBaseRoute(useLocation().pathname);

  const form = useForm<AddZoneFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [TAG_IDENTIFIER]: "",
      [DISPLAY_LABEL]: "",
      [LICENSE_SCOPE]: "",
      [HELPER_TEXT]: "",
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const breadcrumbData = [
    {
      link: `/${basePath}/${routes.masterData}/${routes.tncVariable}`,
      name: `TnC Variables`,
    },
    {
      link: "",
      name: "Add TnC Variable",
    },
  ];

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.masterData}/${routes.tncVariable}`);
  };

  const { mutate: addInfo, isPending: isAddPending } =
    useAddPropertyZoneMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Variable Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["tnc-variables"],
        });
        onBackClick();
      },
    });

  const onSubmit = (data: AddZoneFormValues) => {
    addInfo({ data });
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
  };
};

export default useAdd;
